import { GroupType } from '@bp/bp-graphql-types';
import { ProfileRoles } from '@bp/pim-auth-constants';
import { AddIcon, Grid, GridColumn, GridRow, LazyLoader, Modal, Tab } from '@bp/ui-components';
import classNames from 'classnames';
import { AnnouncementsList } from 'components/AnnouncementsList/AnnouncementsList';
import { AppointmentForm } from 'components/AppointmentForm/AppointmentForm';
import { OpenAssignmentsOverview } from 'components/Assignments/components/OpenAssignmentsOverview/OpenAssignmentsOverview';
import { Timetable } from 'components/Timetable/Timetable';
import dayjs, { Dayjs } from 'dayjs';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'urql';
import { graphql } from '../../client/gql';
import { GroupVisibility } from '../../client/gql/graphql';
import { AnnouncementForm, type AnnouncementFormType } from '../../components/AnnouncementForm/AnnouncementForm';
import { BpCard } from '../../components/BpCard/BpCard';
import { BpHeader } from '../../components/BpHeader/BpHeader';
import { BpPage } from '../../components/BpPage/BpPage';
import { DashboardEvents } from '../../components/Home/DashboardEvents';
import { PreviewData } from '../../components/Home/PreviewData';
import { LinkList } from '../../components/LinkList/LinkList';
import { useGetAnnouncements } from '../../hooks/matrix/useGetAnnouncements';
import { useGetMatrixRoomByName } from '../../hooks/matrix/useGetMatrixRoomByName';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { backendApi } from '../../utils/backendApi';
import { getDaytimeGreeting } from '../../utils/dateCalculations';
import { NotificationReason, RoomNames } from '../../utils/matrixClient';
import { showErrorToast } from '../../utils/showErrorToast';
import styles from './Home.module.scss';

const HomePageQueryDocument = graphql(`
  query HomePageData($calendarEventWhere: CalendarEventWhere!, $groupWhere: GroupWhere!, $courseWhere: GroupWhere!) {
    ...OpenAssignmentQueryFields
    ...PreviewDataQueryFields
    ...DashboardEventsQueryFields
    ...DashboardEventsFields
  }
`);

export const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organizationUuids = pimAuthClaims.getAllowedOrganizationUuids();
  const isDesktop = useIsDesktop();

  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [appointmentUuid, setAppointmentUuid] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Dayjs>(dayjs());

  const organization = pimAuthClaims.getOrganization();

  const [{ data }] = useQuery({
    query: HomePageQueryDocument,
    variables: {
      profile: profileUuid,
      courseWhere: {
        type: GroupType.Course,
        visibility: GroupVisibility.Full,
        organization: { uuid_IN: organizationUuids },
        AND: [
          {
            OR: [{ editors_SOME: { uuid: profileUuid } }, { viewers_SOME: { uuid: profileUuid } }],
          },
          { OR: [{ inCurrentSchoolyear: true }, { isCommunity: true }] },
        ],
      },
      groupWhere: {
        type: GroupType.Group,
        organization: { uuid_IN: organizationUuids },
        AND: [
          {
            OR: [{ editors_SOME: { uuid: profileUuid } }, { viewers_SOME: { uuid: profileUuid } }],
          },
          { OR: [{ inCurrentSchoolyear: true }, { isCommunity: true }] },
        ],
      },
      calendarEventWhere: {
        AND: [
          {
            holderConnection: {
              Group: {
                node: {
                  visibility: GroupVisibility.Full,
                },
              },
            },
          },
          { OR: [{ categories_NOT_INCLUDES: 'lesson' }, { owner: { uuid: profileUuid } }] },
          {
            OR: [
              {
                holderConnection: {
                  Organization: {
                    node: { uuid: organization.uuid },
                  },
                },
              },
              {
                attendees_SOME: {
                  uuid: profileUuid,
                },
              },
              {
                owner: {
                  uuid: profileUuid,
                },
              },
            ],
          },
        ],
      },
    },
  });

  const permissions = usePermissionChecker();

  const firstName = pimAuthClaims.getProfile()?.firstName ?? '';

  const { getRoom } = useGetMatrixRoomByName();
  const room = getRoom(organizationUuid, RoomNames.Organization);

  const announcements = useGetAnnouncements(room);

  const handleAnnouncementFormSubmit = async (values?: AnnouncementFormType) => {
    if (values) {
      setLoading(true);
      const resp = await backendApi.notify({
        type: NotificationReason.OrganizationAnnouncement,
        content: values.message,
        title: values.title,
        organizationUuid: organizationUuid,
      });

      if (resp.ok) setAnnouncementModalOpen(false);

      if (!resp.ok)
        showErrorToast({
          name: '',
          graphQLErrors: [],
          message: resp.statusText,
        });

      setLoading(false);
    }
  };

  function handleEventClicked(groupUuid?: string | null) {
    if (!groupUuid) return;
    navigate(`/courses/${groupUuid}`);
  }

  const teacherTabs = [
    {
      title: t('common.calendar'),
      value: 'calendar',
      content: (
        <DashboardEvents
          query={data}
          isOrganizationAdmin={permissions?.isOrganizationAdmin(organization) ?? false}
          profileUuid={profileUuid}
          onAdd={() => setAppointmentModalOpen(true)}
          onEdit={(uuid) => {
            setAppointmentUuid(uuid);
            setAppointmentModalOpen(true);
          }}
        />
      ),
    },
    {
      content: (
        <div className={classNames(styles.tab, styles['timetable-tab'], styles.teacher)}>
          <Suspense fallback={<LazyLoader embedded forceHeight='768px' />}>
            <Timetable
              showDaySelect
              showLegend
              showHeader
              days={isDesktop ? 5 : 1}
              pagingInterval={isDesktop ? 7 : 1}
              context={'dashboard'}
              onEventClick={({ groupUuid }) => handleEventClicked(groupUuid)}
              selectedDay={selectedDay}
              setSelectedDay={setSelectedDay}
              eventColorSource={'class'}
            />
          </Suspense>
        </div>
      ),
      title: t('timetable.title', { count: 1 }),
      value: 'timetable',
    },
  ];

  const studentTabs = [
    {
      content: (
        <div className={classNames(styles.tab, styles['gantt-tab'])}>
          <OpenAssignmentsOverview
            courseUuid={null}
            daysToShow={5}
            onNavigate={async (courseUuid, uuid) => {
              navigate(`/courses/${courseUuid}/assignments/${uuid}`);
            }}
            query={data}
          />
        </div>
      ),
      title: t('assignments.title', { count: 2 }),
      value: 'assignments',
    },
    {
      content: (
        <div className={classNames(styles.tab, styles['timetable-tab'], styles.teacher)}>
          <Suspense fallback={<LazyLoader embedded forceHeight='500px' />}>
            <Timetable
              context={'dashboard'}
              showDaySelect
              showLegend
              showHeader
              days={isDesktop ? 5 : 1}
              pagingInterval={isDesktop ? 7 : 1}
              selectedDay={selectedDay}
              setSelectedDay={setSelectedDay}
              eventColorSource={'subject'}
            />
          </Suspense>
        </div>
      ),
      title: t('timetable.title', { count: 1 }),
      value: 'timetable',
    },
  ];

  const classes = classNames(styles.home, {
    [styles['no-bar']]: !permissions?.canCreateCourse(),
  });

  return (
    <BpPage
      noHeadlineMargin
      className={classes}
      title={getDaytimeGreeting(firstName)}
      isForbidden={false}
      organization={pimAuthClaims.getOrganization()}
    >
      <Grid>
        <GridRow mobileGap='var(--grid-column-gap)'>
          <PreviewData query={data} />
          <GridColumn width={8}>
            <GridRow spacingTop='none'>
              <BpCard className={styles['calendar-card']} noPadding minHeight='584px'>
                <Suspense fallback={<LazyLoader embedded transparent />}>
                  <Tab
                    className={styles['tab-header']}
                    tabs={
                      permissions?.hasOrganizationRole(ProfileRoles.Student, pimAuthClaims.getOrganization())
                        ? studentTabs
                        : teacherTabs
                    }
                  />
                </Suspense>
              </BpCard>
            </GridRow>
            <GridRow mobileGap='var(--grid-column-gap)'>
              <GridColumn width={6}>
                <BpCard
                  noPadding
                  fixedHeight='280px'
                  canScroll
                  header={{
                    headline: t('announcements.title'),
                    actions: permissions?.canCreateOrganizationAnnouncment({
                      uuid: organizationUuid,
                    })
                      ? [
                          {
                            text: t('common.add'),
                            icon: <AddIcon />,
                            callback: () => {
                              setAnnouncementModalOpen(true);
                            },
                          },
                        ]
                      : [],
                  }}
                >
                  <Suspense fallback={<LazyLoader embedded transparent />}>
                    <AnnouncementsList
                      emptyStateSettings={{ hideIcon: true, forcedHeight: '' }}
                      announcements={announcements}
                      hasPermissionToDelete={
                        permissions?.canCreateOrganizationAnnouncment({
                          uuid: organizationUuid,
                        }) ?? false
                      }
                    />
                  </Suspense>
                </BpCard>
              </GridColumn>
              <GridColumn width={6}>
                <BpCard fixedHeight='280px' header={{ headline: t('links.title') }}>
                  <LinkList />
                </BpCard>
              </GridColumn>
            </GridRow>
          </GridColumn>
        </GridRow>
      </Grid>

      <Modal
        isOpen={appointmentModalOpen}
        onRequestClose={() => {
          setAppointmentUuid(undefined);
          setAppointmentModalOpen(false);
        }}
        isFormModal
        hideHeader
        hideFooter
      >
        <Suspense fallback={<LazyLoader embedded transparent forceHeight='40vh' />}>
          <AppointmentForm
            organizationUuid={organizationUuid}
            holderUuid={organizationUuid}
            appointmentUuid={appointmentUuid}
            conferenceContext='Organization'
            isModal
            onClose={() => {
              setAppointmentUuid(undefined);
              setAppointmentModalOpen(false);
            }}
          />
        </Suspense>
      </Modal>

      <Modal
        isOpen={announcementModalOpen}
        onRequestClose={() => setAnnouncementModalOpen(false)}
        isFormModal
        hideFooter
        title={t('announcements.new')}
        width='s'
      >
        <Suspense fallback={<LazyLoader embedded transparent />}>
          <AnnouncementForm
            onSubmit={handleAnnouncementFormSubmit}
            loading={loading}
            closeModal={() => setAnnouncementModalOpen(false)}
          />
        </Suspense>
      </Modal>
    </BpPage>
  );
};
